/* eslint-disable */
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import Pagination from 'components/Pagination/Pagination';
import { useState } from 'react';
import useSWR from 'swr';
import { formatDate } from 'utils/date';
import { usersService } from 'views/services/usersService';
import { IUserLog, User } from 'views/types/typeUser';
import clsx from 'clsx';
import { getOperationType, getStatus, isBalanceLog } from 'utils/logUtils';

const columnHelper = createColumnHelper<IUserLog>();

const columns = [
  columnHelper.accessor('entityType', {
    header: 'Operation',
    cell: info => {
      const row = info.row.original;
      const operationType = getOperationType(row);

      const getOperationStyle = (type: string) => {
        if (type.includes('Deposit') || type.includes('Card Top-Up')) {
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        }
        if (type.includes('Withdraw') || type.includes('Card Withdraw')) {
          return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        }
        if (
          type.includes('2FA') ||
          type.includes('Email') ||
          type.includes('KYC')
        ) {
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        }
        if (type.includes('Blocked') || type.includes('AML')) {
          return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        }
        if (type.includes('Swap') || type.includes('Card Transaction')) {
          return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        }
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      };

      return (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium',
            getOperationStyle(operationType)
          )}
        >
          {operationType}
        </span>
      );
    },
  }),
  columnHelper.display({
    id: 'details',
    header: 'Details',
    cell: info => {
      const row = info.row.original;
      if (isBalanceLog(row)) {
        const currency = row.extra?.currency ?? '';
        const admin =
          row.extra?.adminName ||
          row.extra?.adminLogin ||
          row.extra?.adminId ||
          '';
        return (
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {currency} {row.old_value} → {row.new_value}
            {admin && <span className="ml-1 text-gray-500">(by {admin})</span>}
          </span>
        );
      }
      return <span className="text-gray-500">--</span>;
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Date + time',
    cell: info => (
      <span className="font-medium text-gray-700 dark:text-gray-300">
        {formatDate(info.getValue(), 'DD.MM.YYYY HH:mm')}
      </span>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => {
      const row = info.row.original;
      let status = getStatus(row);

      if (
        row.entityType === 'REFERRAL_TRANSACTION_REWARD' ||
        (row.entityType === 'USER' && row.field === 'is_two_factor_enabled') ||
        isBalanceLog(row)
      ) {
        status = 'Completed';
      }

      const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
          case 'completed':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
          case 'pending':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
          case 'processing':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
          case 'rejected':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
          case 'failed':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
          case 'blocked':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
          case 'refunded':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
          default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
      };

      if (status === '--') {
        return <span className="text-gray-500">--</span>;
      }

      return (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium',
            getStatusStyle(status)
          )}
        >
          {status}
        </span>
      );
    },
  }),
];

export const UserLogs = ({ user }: { user: User }) => {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const { data: logsData, isLoading } = useSWR(
    `/logs?page=${page}&limit=${itemsPerPage}&filter.user_entity_id=$eq:${user.id}`,
    () =>
      usersService.getPaginatedLogsList({
        page,
        pageSize: itemsPerPage,
        userId: user.id,
      }),
    {
      refreshInterval: 10000,
    }
  );

  const table = useReactTable({
    data: logsData?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  const totalPages = Math.ceil((logsData?.meta.totalItems ?? 0) / itemsPerPage);

  return (
    <>
      {isLoading ? (
        <span>Loading...</span>
      ) : logsData?.data?.length === 0 ? (
        <div className="text-center py-8">
          <div className="font-semibold text-gray-500">
            No logs found for this user
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-h-12 min-w-full border border-gray-300">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="bg-white pb-2 px-4 pt-4 text-start dark:bg-navy-800"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="[&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900"
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="p-2 px-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={logsData?.meta.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalName="Total Logs"
            isBottomIndicators
          />
        </div>
      )}
    </>
  );
};
